/**
 * Per-opcode handlers for the `.TTM` execution core — one small function per
 * opcode (or small opcode family), dispatched by `handleInstruction` through
 * a lookup table rather than one giant switch. Split out of
 * ttm-interpreter.ts purely for size; see that file's module doc for the
 * behavioural rationale (why this is a linear player, the timing model, the
 * dgds-ported quirks each handler below implements).
 */

import type { TtmInstruction, TtmPoint } from "./ttm.ts";
import { MS_PER_FRAME, type TtmBufferId, type TtmCommand, type TtmEnv, type TtmRect, type TtmSpriteFlip } from "./ttm-interpreter-types.ts";

/** The mutable per-tick fields threaded through every handler; mirrors `TtmRunState` minus `script`/`pc`/`frameIndex`/`done`. */
export interface TickState {
  delayFrames: number;
  brush: number;
  bmpSlot: number;
  palSlot: number;
  songSlot: number;
  sndSlot: number;
  getPutSlot: number;
  fg: number;
  bg: number;
  clip: TtmRect;
  readonly bmpSlotAsset: Map<number, string>;
}

function ints(ins: TtmInstruction): readonly number[] | undefined {
  return ins.args.form === "ints" ? ins.args.values : undefined;
}

function str(ins: TtmInstruction): string | undefined {
  return ins.args.form === "string" ? ins.args.value : undefined;
}

function points(ins: TtmInstruction): readonly TtmPoint[] | undefined {
  return ins.args.form === "points" ? ins.args.points : undefined;
}

export function unknownCommand(ins: TtmInstruction): TtmCommand {
  return { kind: "unknown", op: ins.op, name: ins.name, args: ins.args };
}

/** An opcode handler: mutates `state` as needed and returns the command to emit, or `undefined` for a valid instruction with no command (a pure state setter). */
type OpHandler = (state: TickState, env: TtmEnv, ins: TtmInstruction) => TtmCommand | undefined;

/** Guards a fixed-arity `"ints"` handler: reads `args`, and falls back to `unknownCommand` for anything malformed (missing/short/wrong-form args) rather than throwing or silently dropping the instruction. */
function withInts(minArgs: number, run: (values: readonly number[], state: TickState, env: TtmEnv, ins: TtmInstruction) => TtmCommand | undefined): OpHandler {
  return (state, env, ins) => {
    const a = ints(ins);
    return a && a.length >= minArgs ? run(a, state, env, ins) : unknownCommand(ins);
  };
}

/** Same guard, for the inline-string opcodes (`LOAD *`). */
function withString(run: (value: string, state: TickState, env: TtmEnv) => TtmCommand): OpHandler {
  return (state, env, ins) => {
    const value = str(ins);
    return value === undefined ? unknownCommand(ins) : run(value, state, env);
  };
}

// -- loads: LOAD SCR/BMP/PAL/SONG/SND resolve the currently-selected slot --

const handleLoadScr = withString((id, _state, env) => ({ kind: "loadScreen", id, size: env.resolveScreen(id) }));

const handleLoadBmp = withString((id, state) => {
  state.bmpSlotAsset.set(state.bmpSlot, id);
  return { kind: "loadSpriteSheet", slot: state.bmpSlot, id };
});

const handleLoadPal = withString((id, state) => ({ kind: "loadPalette", slot: state.palSlot, id }));
const handleLoadSong = withString((id, state) => ({ kind: "loadSong", slot: state.songSlot, id }));
/** `0xF070 LOAD SND/SX` — Red Baron-only, absent from dgds's own opcode table (docs/formats/ttm-animation.md). */
const handleLoadSnd = withString((id, state) => ({ kind: "loadSoundBank", slot: state.sndSlot, id }));

// -- selectors: state only, except SELECT PAL (see TtmCommand's "setPalette" doc) --

const handleSetBrush = withInts(1, (a, state) => {
  state.brush = a[0]!;
  return undefined;
});
const handleSelectBmp = withInts(1, (a, state) => {
  state.bmpSlot = a[0]!;
  return undefined;
});
const handleSelectPal = withInts(1, (a, state) => {
  state.palSlot = a[0]!;
  return { kind: "setPalette", slot: state.palSlot };
});
const handleSelectSong = withInts(1, (a, state) => {
  state.songSlot = a[0]!;
  return undefined;
});
/** dgds: "sets a global that seems to be never used" (REPORTED no-op) — consumed, no command. */
const handleSetScene = withInts(1, () => undefined);
const handleSelectSndSlot = withInts(1, (a, state) => {
  state.sndSlot = a[0]!;
  return { kind: "selectSoundSlot", slot: state.sndSlot };
});
const handleSetGetPutNum = withInts(1, (a, state) => {
  state.getPutSlot = a[0]!;
  return undefined;
});
const handleSetDrawColors = withInts(2, (a, state) => {
  state.fg = a[0]!;
  state.bg = a[1]!;
  return undefined;
});

// -- timing --

const handleSetDelay = withInts(1, (a, state) => {
  state.delayFrames = a[0]!;
  return { kind: "delay", frames: state.delayFrames, ms: state.delayFrames * MS_PER_FRAME };
});

// -- draw window: dgds's inclusive-max wire form, normalized to a standard rect --

const handleSetClipWindow = withInts(4, (a, state) => {
  const [x, y, xMax, yMax] = a as [number, number, number, number];
  state.clip = { x, y, width: xMax + 1 - x, height: yMax + 1 - y };
  return { kind: "setClipWindow", clip: state.clip };
});

// -- music / palette fades --

const handleStopMusic: OpHandler = () => ({ kind: "stopMusic" });
/** `0x0230` — unimplemented in dgds too (falls to its own `default:` case); named by analogy to `0x0220 STOP CURRENT MUSIC`'s identical 0-arg shape. */
const handleFadeMusic: OpHandler = () => ({ kind: "fadeMusic" });
const handleFadeOut = withInts(4, (a) => ({ kind: "fadeOut", colorStart: a[0]!, colorCount: a[1]!, targetColor: a[2]!, speed: a[3]! }));
const handleFadeIn = withInts(4, (a) => ({ kind: "fadeIn", colorStart: a[0]!, colorCount: a[1]!, targetColor: a[2]!, speed: a[3]! }));

// -- sound effects --

const handlePlaySfx = withInts(1, (a) => ({ kind: "playSound", id: a[0]! }));
const handleStopSfx = withInts(1, (a) => ({ kind: "stopSound", id: a[0]! }));

// -- buffers / paging --

const handleSaveBackground: OpHandler = () => ({ kind: "saveBackground" });
const handleFlipModeOn: OpHandler = () => ({ kind: "pageFlip", enabled: true });
const handleFlipModeOff: OpHandler = () => ({ kind: "pageFlip", enabled: false });
const handleStoreArea = withInts(4, (a) => ({ kind: "storeArea", x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]! }));
/** Slot comes from state (`0x1120 SET GETPUT NUM`), not this instruction's own args — unlike `DRAW GETPUT`. */
const handleSaveGetPutRegion = withInts(4, (a, state) => ({ kind: "saveGetPutRegion", slot: state.getPutSlot, x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]! }));
const handleDrawGetPut = withInts(1, (a) => ({ kind: "drawGetPut", slot: a[0]! }));
const handleCopyBuffer = withInts(6, (a) => ({
  kind: "copyBuffer",
  x: a[0]!,
  y: a[1]!,
  w: a[2]!,
  h: a[3]!,
  srcBuffer: a[4] as TtmBufferId,
  dstBuffer: a[5] as TtmBufferId,
}));

// -- primitive draws --

const handleDrawPixel = withInts(2, (a, state) => ({ kind: "drawPixel", x: a[0]!, y: a[1]!, color: state.fg, clip: state.clip }));
const handleDrawLine = withInts(4, (a, state) => ({ kind: "drawLine", x1: a[0]!, y1: a[1]!, x2: a[2]!, y2: a[3]!, color: state.fg, clip: state.clip }));
/** See TtmCommand's `drawFilledRect` doc for the fill+border fallthrough this ports from dgds's switch. */
const handleDrawFilledRect = withInts(4, (a, state) => ({ kind: "drawFilledRect", x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]!, fg: state.fg, bg: state.bg, clip: state.clip }));
const handleDrawEmptyRect = withInts(4, (a, state) => ({ kind: "drawEmptyRect", x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]!, color: state.fg, clip: state.clip }));
const handleDrawFilledCircle = withInts(4, (a, state) => ({ kind: "drawFilledCircle", x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]!, fg: state.fg, bg: state.bg, clip: state.clip }));
const handleDrawEmptyCircle = withInts(4, (a, state) => ({ kind: "drawEmptyCircle", x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]!, color: state.fg, clip: state.clip }));

const SPRITE_FLIP: ReadonlyMap<number, TtmSpriteFlip> = new Map([
  [0xa510, "v"],
  [0xa520, "h"],
  [0xa530, "hv"],
]);

/** Resolves dgds's 2-/4-/6-arg `DRAW SPRITE` forms — Red Baron's corpus only ever uses the 2-arg (default brush/bmp slot) form, but the others are ported too. */
const handleDrawSprite = withInts(2, (a, state, env, ins) => {
  let frame: number;
  let sheet: number;
  let dstSize: { width: number; height: number } | undefined;
  if (a.length >= 6) {
    frame = a[2]!;
    sheet = a[3]!;
    dstSize = { width: a[4]!, height: a[5]! };
  } else if (a.length >= 4) {
    frame = a[2]!;
    sheet = a[3]!;
  } else {
    frame = state.brush;
    sheet = state.bmpSlot;
  }
  const id = state.bmpSlotAsset.get(sheet);
  return {
    kind: "drawSprite",
    sheet,
    id,
    frame,
    x: a[0]!,
    y: a[1]!,
    flip: SPRITE_FLIP.get(ins.op),
    dstSize,
    size: id !== undefined ? env.resolveSpriteFrame(id, frame) : undefined,
    clip: state.clip,
  };
});

/** Both `DRAW EMPTY/FILLED POLY` use only `state.fg` in dgds (`drawLine`/`drawPolygonScan`), never `bg`. */
function handleDrawPoly(kind: "drawFilledPoly" | "drawEmptyPoly"): OpHandler {
  return (state, _env, ins) => {
    const pts = points(ins);
    return pts ? { kind, points: pts, color: state.fg, clip: state.clip } : unknownCommand(ins);
  };
}

const handleWipe = withInts(4, (a, _state, _env, ins) => ({ kind: "wipe" as const, style: ins.name ?? `0x${ins.op.toString(16)}`, x: a[0]!, y: a[1]!, w: a[2]!, h: a[3]! }));

const OP_HANDLERS: ReadonlyMap<number, OpHandler> = new Map<number, OpHandler>([
  [0xf010, handleLoadScr],
  [0xf020, handleLoadBmp],
  [0xf050, handleLoadPal],
  [0xf060, handleLoadSong],
  [0xf070, handleLoadSnd],

  [0x1030, handleSetBrush],
  [0x1050, handleSelectBmp],
  [0x1060, handleSelectPal],
  [0x1090, handleSelectSong],
  [0x10a0, handleSetScene],
  [0x10c0, handleSelectSndSlot],
  [0x1120, handleSetGetPutNum],
  [0x2000, handleSetDrawColors],

  [0x1020, handleSetDelay],
  [0x4000, handleSetClipWindow],

  [0x0220, handleStopMusic],
  [0x0230, handleFadeMusic],
  [0x4110, handleFadeOut],
  [0x4120, handleFadeIn],

  [0x1300, handlePlaySfx],
  [0x1310, handleStopSfx],

  [0x0020, handleSaveBackground],
  [0x0500, handleFlipModeOn],
  [0x0510, handleFlipModeOff],
  [0x4200, handleStoreArea],
  [0x4210, handleSaveGetPutRegion],
  [0xa600, handleDrawGetPut],
  [0xb600, handleCopyBuffer],

  [0xa000, handleDrawPixel],
  [0xa0a0, handleDrawLine],
  [0xa100, handleDrawFilledRect],
  [0xa110, handleDrawEmptyRect],
  [0xa400, handleDrawFilledCircle],
  [0xa420, handleDrawEmptyCircle],
  [0xa500, handleDrawSprite],
  [0xa510, handleDrawSprite],
  [0xa520, handleDrawSprite],
  [0xa530, handleDrawSprite],
  [0xaf10, handleDrawPoly("drawEmptyPoly")],
  [0xaf20, handleDrawPoly("drawFilledPoly")],
  [0xa010, handleWipe],
  [0xa020, handleWipe],
  [0xa030, handleWipe],
  [0xa040, handleWipe],
  [0xa050, handleWipe],
  [0xa060, handleWipe],
  [0xa070, handleWipe],
  [0xa080, handleWipe],
  [0xa090, handleWipe],
]);

/**
 * Dispatches one instruction (never `0x0FF0 FINISH FRAME` — the caller
 * handles the tick boundary itself) to its handler, mutating `state` and
 * returning the command to emit (or `undefined` for a pure state setter).
 * An opcode with no registered handler — nameless, or a REPORTED-only/
 * unimplemented-in-dgds opcode this interpreter has no behaviour for (e.g.
 * `0x2010 SET FRAME`, see ttm-interpreter.ts's module doc) — surfaces as
 * `{kind:"unknown"}` rather than throwing.
 */
export function handleInstruction(state: TickState, env: TtmEnv, ins: TtmInstruction): TtmCommand | undefined {
  const handler = OP_HANDLERS.get(ins.op);
  return handler ? handler(state, env, ins) : unknownCommand(ins);
}
